import type { Command } from 'commander';
import { existsSync } from 'node:fs';
import { buildUserAgent, createApiClient } from '@wport/core';
import { CLI_SOURCE, resolveContext, type ResolvedContext } from '../lib/global-opts';
import { getConfigPath } from '../lib/config-store';
import { ExitCode } from '../lib/errors';
import { loadPersonalCredentials } from '../lib/credentials-store';
import { requestDeviceCode, type OauthRequestOptions } from '../lib/oauth';
import { currentChannel } from '../lib/channel';

/**
 * Query params the server accepts syntactically but silently ignores — surfacing them
 * here is the whole point: an agent reading `wport doctor` learns not to try to control
 * sort order (it can't), instead of discovering it the hard way via wrong-but-no-error
 * results. The CLI deliberately doesn't expose flags for these.
 */
export const SILENT_IGNORED_PARAMS = ['orderBy', 'order'];

/**
 * Capability boundary an agent MUST know before scripting talent workflows (issue #106 P1-4):
 * an Enterprise API Key can only read/act on the company's *applied* talent pool. The visit
 * tab is not available yet (`talents list --tab visit` → 400), and there is no active candidate
 * search over an API Key — the public `/api/search-talent` endpoint requires an interactive JWT
 * (company mode) session and returns 401 for an API Key. Surfacing this in `doctor` stops an
 * agent from discovering these boundaries the hard way via a bare 40x.
 */
export const ENTERPRISE_TALENT_BOUNDARY_NOTES = [
	'`enterprise talents` covers your APPLIED pool only (list / view / respond).',
	'The visit tab is not available yet: `talents list --tab visit` returns 400.',
	'There is NO active candidate search over an API Key. `/api/search-talent` needs an',
	'interactive JWT (company-mode) session and returns 401 for an API Key — by design,',
	'not a setup error. Do not script talent-sourcing against an Enterprise API Key.',
];

export function registerDoctorCommand(program: Command): void {
	program
		.command('doctor')
		.description(
			'Diagnose CLI setup: resolved config, personal login state, server reachability, schema fingerprint, and known server quirks.'
		)
		.action(async (_opts: unknown, command: Command) => {
			const ctx = resolveContext(command);
			const reachable = await runDoctor(ctx);
			if (!reachable) process.exit(ExitCode.ServerOrNetworkError);
		});
}

/**
 * doctor 主體邏輯抽成獨立函式（沿 performLogin/performWhoami 慣例），讓輸出內容可被測試斷言，
 * 不必透過 commander 整條 parse 流程重跑一次。回傳值 = 既有「公開 API 是否可達」判斷，
 * 決定 registerDoctorCommand 是否以非 0 exit code 結束——AS 連通性檢查只是輔助診斷資訊，
 * 不影響這個回傳值（見 probeAuthServer 註解）。
 */
export async function runDoctor(ctx: ResolvedContext): Promise<boolean> {
	const line = (s = '') => process.stdout.write(s + '\n');

	line(`wport-cli ${__CLI_VERSION__}`);
	line(`  bundled schema fingerprint: ${__SCHEMA_HASH__}`);
	line('');

	line('Resolved configuration:');
	line(`  API base URL: ${ctx.baseUrl}`);
	line(`  channel:      ${currentChannel()}`);
	line(`  locale:       ${ctx.locale}`);
	line(`  timeout:      ${ctx.timeoutMs}ms`);
	const cfgPath = getConfigPath();
	line(`  config file:  ${cfgPath}${existsSync(cfgPath) ? '' : ' (not present)'}`);
	line('');

	line('Personal account (OAuth):');
	for (const l of describePersonalLoginLines()) line(`  ${l}`);
	line('');

	line('Server connectivity:');
	const reachable = await probeServer(ctx, line);
	await probeAuthServer(ctx, line);
	line('');

	line('Known server behaviours (read this before scripting an agent):');
	line(
		`  • Sort is server-controlled. These query params are silently ignored: ${SILENT_IGNORED_PARAMS.join(', ')}.`
	);
	line('  • jobs search sorts by publish date, or by relevance when --keyword is set.');
	line('  • jobs view --batch caps parallelism (default 5, max 20) to stay friendly to the API.');
	line('');

	line('Enterprise talent scope (Enterprise API Key):');
	for (const note of ENTERPRISE_TALENT_BOUNDARY_NOTES) line(`  • ${note}`);
	line('');

	line('Schema drift:');
	line('  The fingerprint above identifies the OpenAPI contract this CLI was built against.');
	line('  Automated drift detection needs a server-side schema-version endpoint, which is');
	line('  not available yet — for now, compare fingerprints manually after a server release. [TODO]');

	return reachable;
}

/**
 * 個人線登入態：只讀 credentials 檔、不打任何請求、不觸發 refresh —— doctor 是唯讀診斷工具，
 * 若順手在這裡 refresh 或呼叫 API，會讓「跑一次 doctor」產生副作用（消耗 access token 續期，
 * 甚至因為過期/被撤銷的 refresh token 觸發整批撤銷），這不是診斷工具該做的事。
 *
 * 整段包 try/catch：credentials.json 壞損時 `loadPersonalCredentials` 會丟 CliError
 * (ConfigCorrupt)——doctor 存在的目的正是要診斷「壞掉的東西」，不能自己被這個錯誤拖著
 * 整個指令一起死在這個區塊，讓 Server connectivity 等後續檢查都沒機會跑。
 */
function describePersonalLoginLines(): string[] {
	try {
		const creds = loadPersonalCredentials();
		if (!creds) return ['not logged in (run `wport login` to sign in)'];

		const expiresAtMs = Date.parse(creds.expires_at);
		if (Number.isNaN(expiresAtMs)) {
			return [
				'logged in',
				`access token expiry is unreadable ("${creds.expires_at}") — try \`wport login --force\` to re-authenticate`,
			];
		}
		const expired = expiresAtMs <= Date.now();
		return [
			'logged in',
			expired
				? `access token expired at ${creds.expires_at} (refreshes automatically on next request)`
				: `access token valid until ${creds.expires_at}`,
		];
	} catch (err) {
		return [`personal credentials file appears corrupted: ${err instanceof Error ? err.message : String(err)}`];
	}
}

/**
 * Lightweight reachability probe: a 1-result search hits the real public endpoint
 * without pulling a meaningful payload. A network-layer failure is a hard "unreachable"
 * (caller exits non-zero); an HTTP response of any status still proves the host is
 * reachable, so we report the status but don't treat it as a connectivity failure.
 */
async function probeServer(
	ctx: { baseUrl: string; locale: string; timeoutMs: number },
	line: (s?: string) => void
): Promise<boolean> {
	try {
		const client = createApiClient({
			baseUrl: ctx.baseUrl,
			locale: ctx.locale,
			timeoutMs: ctx.timeoutMs,
			userAgent: buildUserAgent('wport-cli', __CLI_VERSION__),
			source: CLI_SOURCE,
		});
		const { response } = await client.GET('/api/jobs/search', { params: { query: { pageSize: 1 } } });
		if (response.ok) {
			line(`  ✓ reachable (HTTP ${response.status})`);
		} else {
			line(`  ! reachable, but server responded HTTP ${response.status}`);
		}
		return true;
	} catch (err) {
		line(`  ✗ unreachable: ${err instanceof Error ? err.message : String(err)}`);
		return false;
	}
}

/** device_name 送給 AS 讓伺服器端的 session/device 列表看得出這是診斷探測，不是真的登入嘗試。 */
const DOCTOR_PROBE_DEVICE_NAME = 'wport doctor (connectivity check)';

/**
 * AS（OAuth Authorization Server）連通性：`POST /oauth/device/code` 帶 device_name，走一次完整
 * 的 device-code 請求形狀。這是唯一不需要 Authorization、對「只是在跑 doctor」這件事本身無害的
 * unauthenticated 端點——產生的 device code 沒人會去 poll，`expires_in` 秒後在伺服器端自然失效，
 * 不留殘留狀態。失敗只印一行、不拋出、不中斷其餘檢查，也不影響 runDoctor 的回傳值——AS 連通性
 * 目前只是輔助診斷資訊，doctor 的整體 exit code 仍只看既有的公開 API 探測（probeServer）。
 */
async function probeAuthServer(
	ctx: { baseUrl: string; locale: string; timeoutMs: number },
	line: (s?: string) => void
): Promise<void> {
	const opts: OauthRequestOptions = { baseUrl: ctx.baseUrl, locale: ctx.locale, timeoutMs: ctx.timeoutMs };
	try {
		await requestDeviceCode(opts, DOCTOR_PROBE_DEVICE_NAME);
		line('  ✓ auth server reachable (device code endpoint)');
	} catch (err) {
		line(`  ! auth server check failed: ${err instanceof Error ? err.message : String(err)}`);
	}
}

// Internal helpers exposed for tests. Not part of the public CLI API.
export const __test__ = { describePersonalLoginLines };
